import { registerPlugin } from '@capacitor/core'
import { getNativePlatform, isNativeApp } from '../../utils/platform'
import type { CallPurpose } from '../../types/voice-call'
import { pushDiagnostics } from '../../stores/push-diagnostics-store'
import { recordCallFlow } from '../../debug/call-flow-log'
import { createCarStartTapLedger } from '../../utils/car-start-dedupe'

/**
 * In-app Capacitor plugin (frontend/native/android/CallPlugin.java) providing
 * phone-style call behaviour the WebView cannot deliver on its own:
 *
 *  - An ongoing-call **foreground service** + **wake locks** so an active call
 *    survives screen-off, proximity (phone-to-ear), and app backgrounding. The
 *    WebView/Chime audio session is otherwise paused/killed by Android when the
 *    screen turns off, which previously dropped the call.
 *  - A **full-screen-intent CallStyle** incoming-call notification that drops
 *    down as a heads-up, shows over the lock screen, rings, vibrates, and
 *    offers Answer/Decline — RingCentral-style — while the app process is alive
 *    (foreground or background). Killed-app delivery still depends on the FCM
 *    high-importance notification (documented limit).
 *
 * Every method is best-effort: on web, or on an older APK without the plugin,
 * the wrappers resolve to no-ops so the call flow keeps working.
 */
type NativeCallPluginApi = {
  /**
   * Anchor an active call in the OS. Android: ongoing-call foreground service
   * + wake locks. iOS: report an outgoing CXStartCallAction so CallKit keeps
   * the app's audio alive in the background and shows the system green
   * pill/bar as a return-to-call surface (needs callId; ignored by Android).
   */
  startOngoingCall(options: {
    video: boolean
    peerName: string
    callId?: string
    peerId?: string
    group?: boolean
    /**
     * Android: true ONLY for the caller's dial-time report — the Telecom
     * connection starts DIALING with system ringback (the pre-org-tone owner).
     * Omitted/false = representing an already answered/connecting call: the
     * connection is created ACTIVE, never DIALING — a DIALING keep-alive made
     * the CALLEE's phone play a dial tone after answering (2026-09-16).
     */
    dialing?: boolean
    /** Android: the dial came from a car tap (a pending car End may apply). */
    fromCar?: boolean
  }): Promise<{ refused?: boolean; reason?: string } | void>
  /**
   * Android: explicit OUTGOING teardown for a dial abandoned before it
   * connected (never touches an incoming ring). Optional on older shells.
   */
  endOutgoingCall?(options: { callId: string; reason?: string; carTapAt?: number }): Promise<void>
  /** Android: the callee answered this dial — cancel the native DIALING stall backstop. */
  noteOutgoingAnswered?(options: { callId: string }): Promise<void>
  /** Android: mirror the signed-in user id (account-switch hygiene). */
  setSignedInUser?(options: { userId: string }): Promise<{ transition?: string }>
  /** Android: wipe the signed-out account's native call state. */
  clearUserState?(options: { reason?: string }): Promise<{ failed?: string[] }>
  /** Stop the foreground service + release wake locks (scoped to `callId` when given). */
  stopOngoingCall(options?: { callId?: string; carTapAt?: number }): Promise<void>
  /** Show the full-screen-intent CallStyle incoming-call notification. */
  showIncomingCall(options: {
    callId: string
    callerName: string
    video: boolean
    callerId?: string
    calleeId?: string
    /** 'normal' | 'on-call' | 'urgent' — surfaces an On-call / Urgent label. */
    callPurpose?: string
  }): Promise<void>
  /**
   * Dismiss the incoming-call notification / full-screen activity.
   * reason 'answered' (Android): the user answered HERE — promote the ringing
   * Telecom connection to ACTIVE instead of destroying it. Destroying it made
   * the later keep-alive re-report the call as OUTGOING (placeCall on the
   * callee — wrong direction on car/BT surfaces, 2026-09-16 telemetry).
   */
  cancelIncomingCall(options: {
    callId: string
    reason?: 'answered' | 'canceled'
    /** Answered call identity — seeds the Telecom caller name (never "Care team"). */
    peerId?: string
    peerName?: string
    callerName?: string
  }): Promise<void>
  /** True when the native plugin is present (vs. an older APK). */
  isAvailable(): Promise<{ available: boolean }>
  /**
   * iOS only: listener-ready handshake. Called right after the JS 'callAction'
   * listener attaches so native re-emits any pending CallKit Answer/End
   * directly to it (cold-start replay that doesn't depend on retained-event
   * timing). Absent on Android — callers must treat it as optional.
   */
  notifyCallActionListenerReady?(): Promise<void>
  /**
   * Return (and clear) a pending Answer/Decline the user tapped while the
   * WebView was paused/cold. Empty object when there is nothing pending.
   */
  consumePendingCallAction(): Promise<{
    callId?: string
    action?: 'answer' | 'decline' | 'dismiss' | 'end'
    ts?: number
    source?: string
    /** Android: answered while the keyguard was locked (video waits for unlock). */
    lockedAnswer?: boolean
  }>
  /**
   * Android: mirror the live call (phase / mute / speaker / video / connect
   * time) for the native lock-screen call screen. Optional on older shells.
   */
  updateLockScreenCall?(options: {
    callId: string
    phase: 'connecting' | 'active' | 'ended'
    muted: boolean
    speaker: boolean
    video: boolean
    connectedAt?: number
  }): Promise<void>
  /** Android: whether the keyguard is locked right now. Optional on older shells. */
  getKeyguardState?(): Promise<{ locked?: boolean }>
  /**
   * Return (and clear) a pending incoming-call invite seeded natively from an
   * FCM data message while the app was killed. Lets JS rebuild the session on
   * cold start. Empty object when there is nothing pending.
   */
  consumePendingIncomingCall(): Promise<{
    callId?: string
    callerId?: string
    calleeId?: string
    callerName?: string
    mediaType?: string
    callPurpose?: string
    conversationId?: string
    /** True when this was an "added to a group call" invite (join existing meeting). */
    roomInvite?: boolean
    /** The room/meeting id to join for a room invite (defaults to callId). */
    joinCallId?: string
    ts?: number
  }>
  /** Android 14+: whether USE_FULL_SCREEN_INTENT is granted (true below 34). */
  canUseFullScreenIntent(): Promise<{ granted: boolean }>
  /** Open the system "Full screen intents" toggle for this app (API 34+). */
  openFullScreenIntentSettings(): Promise<void>
  /** Whether the app is on the battery-optimization (Doze) allowlist. */
  isIgnoringBatteryOptimizations(): Promise<{ ignoring: boolean }>
  /** Show the one-time system dialog to exempt the app from battery optimization. */
  requestIgnoreBatteryOptimizations(): Promise<void>
  /** Android (after build 133): whether a notification channel can alert (importance above none). */
  getNotificationChannelStatus(options: { channelId: string }): Promise<{ enabled: boolean; importance?: number }>
  /** Android (after build 133): open one channel's system settings. */
  openNotificationChannelSettings(options: { channelId: string }): Promise<void>
  /** Register the self-managed Telecom PhoneAccount (idempotent). */
  registerTelecomAccount(): Promise<{ supported: boolean }>
  /** iOS only: the current PushKit VoIP token (empty until PushKit registers). */
  getVoipToken?(): Promise<{ token: string; lastKnownToken?: string }>
  /**
   * iOS only: live CallKit call snapshot (answered stay-alive / ringing UUID)
   * so JS can re-present Active Call after Dynamic Island / deep-link return.
   */
  getActiveCallKitCall?(): Promise<{
    callId?: string
    uuid?: string
    answered?: boolean
    nativeMedia?: boolean
    callerName?: string
    mediaType?: string
    callPurpose?: string
    callerId?: string
    calleeId?: string
    conversationId?: string
    roomInvite?: boolean | string
    joinCallId?: string
  }>
  /**
   * Move the self-managed Telecom connection to ACTIVE (starts the status-bar
   * call chip + timer). Call the moment the Chime media session connects.
   */
  setCallActive(options: { callId: string }): Promise<void>
  /**
   * Disconnect the self-managed Telecom connection so the OS clears the
   * status-bar chip / ringing call. reason: rejected | missed | canceled.
   */
  setCallDisconnected(options: { callId: string; reason?: string; carTapAt?: number }): Promise<void>
  /**
   * Destroy leftover Telecom (Android) / outgoing CallKit (iOS) calls that do
   * not match the live JS session. Omit liveCallId when JS has no live call.
   */
  sweepNativeCalls?(options: { liveCallId?: string | null }): Promise<void>
  /**
   * Android: callIds the server already ended (FCM END / CANCEL / MISSED fan-out
   * landed natively, even while the WebView was dead). Process-lifetime.
   */
  getRemoteEndedCallIds?(): Promise<{ callIds?: string[] }>
  /**
   * iOS + Android: loop the connecting tone (mandatory recorded-call disclosure)
   * through a native player — WKWebView HTML5 is silenced under CallKit, and
   * Android WebView HTML5 is often paused/autoplay-blocked under Telecom.
   * Resolves the duration (ms) of one playthrough; rejects when playback failed
   * (caller falls back to HTML5).
   */
  playConnectingTone?(options: { url?: string; callId?: string }): Promise<{ durationMs: number }>
  /**
   * Loop the caller ringback natively (WebView HTML5 is paused by audio-focus /
   * Telecom / CallKit ownership). Hard-capped by maxDurationMs natively so a
   * suspended WebView can never leave it ringing.
   */
  playRingbackTone?(options: { url?: string; maxDurationMs?: number; callId?: string }): Promise<{
    durationMs: number
    usage?: string
    stream?: string
    aaConnected?: boolean
    uiModeCar?: boolean
    outputHasCar?: boolean
    leaveUnpinned?: boolean
    focusGranted?: boolean
    /**
     * 'telecom' when the car host denied the signalling audio focus and native
     * kept Telecom's ringback as the single owner instead of the (inaudible)
     * org tone — the silent-ringback fallback (call 11be9fd4, 2026-10-06).
     * Absent/undefined = the org tone player owns it as usual.
     */
    owner?: string
  }>
  stopConnectingTone?(options?: { reason?: string }): Promise<void>
  recordCarRecent?(options: {
    peerId: string
    peerName: string
    ts?: number
    avatarUrl?: string | null
  }): Promise<void>
  replaceCarRecents?(options: {
    recents: Array<{ peerId: string; peerName: string; ts?: number; avatarUrl?: string | null }>
  }): Promise<void>
  replaceCarContacts?(options: {
    contacts: Array<{
      peerId: string
      peerName: string
      avatarUrl?: string | null
      address?: string
      lat?: number
      lng?: number
    }>
  }): Promise<void>
  replaceCarMessages?(options: {
    messages: Array<{
      conversationId: string
      title: string
      preview: string
      ts: number
      peerId?: string
    }>
  }): Promise<void>
  replaceCarSessions?(options: {
    sessions: Array<{
      sessionUuid: string
      title: string
      start: number
      end?: number
      address?: string
      lat?: number
      lng?: number
      cancelled?: boolean
    }>
  }): Promise<void>
  clearCarRecents?(): Promise<void>
  notifyCarStartFailed?(options: { message?: string; alreadyOnCall?: boolean }): Promise<void>
  consumePendingCarStartCall?(): Promise<{
    peerId?: string
    peerName?: string
    ts?: number
    cancelledAt?: number
  }>
  /** JS handled the live carStartCall for tap `ts`: native drops its saved copy. */
  ackCarStartCall?(options: { ts: number }): Promise<void>
  getCarStartCancelledAt?(): Promise<{ ts?: number }>
  /**
   * Is a car attached right now? iOS: the CarPlay scene is connected.
   * Android: an Android Auto projection session is live, the UI mode is car,
   * or a car-typed audio output is present (same `carAttached` signal the
   * ringback path uses). Drives the in-car video pause — never audio.
   */
  getCarConnectionState?(): Promise<{
    connected: boolean
    uiModeCar?: boolean
    outputHasCar?: boolean
    carProjection?: boolean
  }>
  addListener(
    eventName: 'callAction',
    listener: (data: {
      callId: string
      action: 'answer' | 'decline' | 'dismiss' | 'end' | 'mute' | 'unmute'
      /** Native surface: telecom | telecom-abort | car | notification | lock-screen | watchdog. */
      source?: string
      /** Android: an answer made while the keyguard was locked. */
      lockedAnswer?: boolean
    }) => void,
  ): Promise<{ remove: () => Promise<void> }>
  /** Android: the device unlocked during a call answered on the lock screen. */
  addListener(
    eventName: 'deviceUnlocked',
    listener: (data: { callId: string; via?: string }) => void,
  ): Promise<{ remove: () => Promise<void> }>
  /** Android: a Telecom connection was destroyed; `source` = teardown origin. */
  addListener(
    eventName: 'connectionDestroyed',
    listener: (data: { callId: string; source?: string }) => void,
  ): Promise<{ remove: () => Promise<void> }>
  addListener(
    eventName: 'callAudioRoute',
    listener: (data: {
      callId: string
      route: 'earpiece' | 'speaker' | 'bluetooth' | 'headset'
      /** The user picked this output on a native surface (lock-screen Speaker). */
      userPick?: boolean
    }) => void,
  ): Promise<{ remove: () => Promise<void> }>
  addListener(
    eventName: 'voipToken',
    listener: (data: { token: string }) => void,
  ): Promise<{ remove: () => Promise<void> }>
  addListener(
    eventName: 'callAudioSessionActivated',
    listener: () => void,
  ): Promise<{ remove: () => Promise<void> }>
  addListener(
    eventName: 'callAudioSessionDeactivated',
    listener: () => void,
  ): Promise<{ remove: () => Promise<void> }>
  addListener(
    eventName: 'nativeIncomingRing',
    listener: (data: { callId: string; active: boolean }) => void,
  ): Promise<{ remove: () => Promise<void> }>
  /**
   * iOS: user returned from CallKit UI (Dynamic Island / green pill / unlock
   * after answer). JS should show Active Call and adopt native media state.
   */
  addListener(
    eventName: 'callKitReturn',
    listener: (data: {
      callId: string
      uuid?: string
      nativeMedia?: boolean
      source?: string
    }) => void,
  ): Promise<{ remove: () => Promise<void> }>
  /**
   * Live telemetry from the native layer (AVAudioSession / CallKit /
   * AudioManager / Telecom / app lifecycle / WKWebView content-process
   * termination). Namespaced `event` label + optional callId + arbitrary state
   * fields; funnelled into the JS call-flow telemetry buffer so everything
   * ships through one path. Fire-and-forget — never gates call behaviour.
   */
  addListener(
    eventName: 'callTelemetry',
    listener: (data: Record<string, unknown>) => void,
  ): Promise<{ remove: () => Promise<void> }>
  addListener(
    eventName: 'connectingToneComplete',
    listener: (data: { durationMs?: number }) => void,
  ): Promise<{ remove: () => Promise<void> }>
  addListener(
    eventName: 'carStartCall',
    listener: (data: { peerId: string; peerName?: string; ts?: number }) => void,
  ): Promise<{ remove: () => Promise<void> }>
  addListener(
    eventName: 'carStartCancelled',
    listener: (data: { ts?: number }) => void,
  ): Promise<{ remove: () => Promise<void> }>
  /** CarPlay / Android Auto attach state changed (see getCarConnectionState). */
  addListener(
    eventName: 'carConnectionChanged',
    listener: (data: { connected: boolean }) => void,
  ): Promise<{ remove: () => Promise<void> }>
}

const CallNative = registerPlugin<NativeCallPluginApi>('CallNative')

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

let availabilityCache: boolean | null = null
/** Older native shells without playConnectingTone — skip retries, use HTML5. */
let nativeConnectingToneUnsupported = false

/** Call ids with an active native Telecom/CallKit incoming ring (FCM or in-app). */
const nativeIncomingRingCallIds = new Set<string>()

/** Mark that the OS-level incoming ring is active for this call (skip JS ringtone). */
export function markNativeIncomingRingActive(callId: string): void {
  if (callId) nativeIncomingRingCallIds.add(callId)
}

/** Clear native-ring tracking when the OS ring is dismissed or answered. */
export function clearNativeIncomingRingActive(callId: string): void {
  if (callId) nativeIncomingRingCallIds.delete(callId)
}

/** True when native Telecom/CallKit is ringing for this call id. */
export function isNativeIncomingRingActive(callId: string): boolean {
  return Boolean(callId && nativeIncomingRingCallIds.has(callId))
}

/**
 * Current CarPlay / Android Auto attach state. False on web, on older native
 * shells without the method, and on any error — car mode must never break a
 * call, only (when detected) pause its video.
 */
export async function getNativeCarConnectionState(): Promise<boolean> {
  if (!isNativeApp()) return false
  try {
    if (typeof CallNative.getCarConnectionState !== 'function') return false
    const state = await CallNative.getCarConnectionState()
    return state?.connected === true
  } catch {
    return false
  }
}

/** Subscribe to CarPlay / Android Auto attach-state changes. Null when unavailable. */
export async function addCarConnectionChangedListener(
  listener: (connected: boolean) => void,
): Promise<{ remove: () => Promise<void> } | null> {
  if (!isNativeApp()) return null
  try {
    return await CallNative.addListener('carConnectionChanged', (data) => {
      listener(data?.connected === true)
    })
  } catch {
    return null
  }
}

/** Whether the native call plugin is compiled into this build. */
export async function isNativeCallSupported(): Promise<boolean> {
  if (!isNativeApp()) return false
  if (availabilityCache !== null) return availabilityCache
  try {
    const { available } = await CallNative.isAvailable()
    availabilityCache = available
    return available
  } catch {
    availabilityCache = false
    return false
  }
}

/**
 * iOS + Android: start the NATIVE connecting-tone loop (recorded-call disclosure).
 * WKWebView HTML5 is silenced under CallKit; Android WebView HTML5 is often
 * paused/autoplay-blocked under Telecom. Returns the duration (ms) of one
 * playthrough, or null when native playback is unavailable/failed — the caller
 * must fall back to the HTML5 tone (and skip retries when the plugin method
 * is missing on an older native build).
 */
export async function playNativeConnectingTone(
  url?: string | null,
  /** The call the disclosure belongs to: another call's teardown never stops it. */
  callId?: string | null,
): Promise<number | null> {
  if (!isNativeApp()) return null
  if (nativeConnectingToneUnsupported) return null
  try {
    if (typeof CallNative.playConnectingTone !== 'function') {
      nativeConnectingToneUnsupported = true
      return null
    }
    const { durationMs } = await CallNative.playConnectingTone({
      ...(url ? { url } : {}),
      ...(callId ? { callId } : {}),
    })
    return Number.isFinite(durationMs) && durationMs > 0 ? durationMs : null
  } catch (error) {
    const msg = errorMessage(error)
    if (/not implemented|unimplemented|UNIMPLEMENTED/i.test(msg)) {
      nativeConnectingToneUnsupported = true
    }
    recordCallFlow('native:connectingTone:failed', {
      error: msg,
      platform: getNativePlatform(),
    })
    return null
  }
}

let nativeRingbackUnsupported = false

/**
 * JS-side ownership of native caller ringback. Set when playRingbackTone
 * resolves with a playable duration; cleared on stop / play failure. Cheap
 * synchronous check so the HTML5 re-kick in startCallWithMedia can stand
 * down while ConnectingTonePlayer already owns the tone.
 */
let nativeRingbackActive = false

/**
 * Native ownership begins at REQUEST, not at started: set synchronously when
 * playRingbackTone is invoked, cleared when the request settles. The native
 * MediaPlayer prepare takes hundreds of ms (782ms observed, call 509d61f1,
 * build 110) and the HTML5 re-kick used to fire inside that
 * requested-but-not-started window — the double dial tone #371 missed. If the
 * native request ultimately FAILS both flags clear and the existing HTML5
 * fallback paths still fire.
 */
let nativeRingbackPending = false

/**
 * Android outgoing dial: the caller ringback must not start before Telecom has
 * the call. Requested at phase 'outgoing', it ran ahead of the permission
 * probe and placeCall, so native's wait-for-DIALING deferral engaged on 1 of
 * 5 car dials and the tone played on the phone (2026-10-20, Denza B5).
 * Registered per callId by expectNativeDialReport, settled when
 * startNativeOngoingCall (dialing) returns.
 */
export const NATIVE_DIAL_REPORT_WAIT_MS = 20_000

type NativeDialReport = { promise: Promise<void>; settle: () => void; settled: boolean }
const nativeDialReports = new Map<string, NativeDialReport>()

function nativeDialReport(callId: string): NativeDialReport {
  const existing = nativeDialReports.get(callId)
  if (existing) return existing
  let release: () => void = () => {}
  const report: NativeDialReport = {
    promise: new Promise<void>((resolve) => {
      release = resolve
    }),
    settle: () => {
      report.settled = true
      release()
    },
    settled: false,
  }
  nativeDialReports.set(callId, report)
  if (nativeDialReports.size > 8) {
    nativeDialReports.delete(nativeDialReports.keys().next().value as string)
  }
  return report
}

/**
 * Android: `callId` is a 1:1 dial that WILL be reported to Telecom
 * (startNativeOngoingCall with dialing) — its ringback waits for that report.
 * Call before the session reaches 'outgoing'. Rings that never become a
 * Telecom dial (the urgent leadership ring) never register, so they never wait.
 */
export function expectNativeDialReport(callId: string): void {
  if (!isNativeApp() || getNativePlatform() !== 'android' || !callId) return
  nativeDialReport(callId)
}

/**
 * Android: resolves once the dial for `callId` is reported to Telecom
 * ('reported') or after `timeoutMs` ('timeout'). 'skipped' at once off
 * Android and for any call no Telecom dial report was expected for
 * (expectNativeDialReport) — an urgent leadership ring sat silent for the
 * full timeout (#476 review). Ringback ownership is claimed while waiting,
 * so the HTML5 re-kick stands down instead of ringing the phone pre-dial.
 */
export async function waitForNativeDialReport(
  callId: string,
  timeoutMs = NATIVE_DIAL_REPORT_WAIT_MS,
): Promise<'reported' | 'timeout' | 'skipped'> {
  if (!isNativeApp() || getNativePlatform() !== 'android' || !callId) return 'skipped'
  const report = nativeDialReports.get(callId)
  if (!report) return 'skipped'
  if (report.settled) return 'reported'
  nativeRingbackPending = true
  let timer: ReturnType<typeof setTimeout> | undefined
  const timedOut = new Promise<'timeout'>((resolve) => {
    timer = setTimeout(() => resolve('timeout'), timeoutMs)
  })
  try {
    return await Promise.race([report.promise.then(() => 'reported' as const), timedOut])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/** Test hook. */
export function resetNativeDialReportsForTests(): void {
  nativeDialReports.clear()
}

/** True while native ringback is playing/owned after a successful playRingbackTone. */
export function isNativeRingbackActive(): boolean {
  return nativeRingbackActive
}

/** True from the playRingbackTone request until native resolves or fails. */
export function isNativeRingbackPending(): boolean {
  return nativeRingbackPending
}

/**
 * Native caller RINGBACK: loops on the native player until stopped (answer /
 * ring end) or the native maxDurationMs cap. Returns one playthrough's
 * duration (ms), or null when unavailable — callers fall back to HTML5.
 */
export async function playNativeRingbackTone(input: {
  url?: string | null
  maxDurationMs?: number
  /** Lets native refuse the tone for a call it already knows is terminal. */
  callId?: string
}): Promise<number | null> {
  if (!isNativeApp()) return null
  if (nativeRingbackUnsupported) {
    nativeRingbackActive = false
    return null
  }
  // Ownership starts NOW (see nativeRingbackPending) — before any await.
  nativeRingbackPending = true
  try {
    if (typeof CallNative.playRingbackTone !== 'function') {
      nativeRingbackUnsupported = true
      nativeRingbackActive = false
      return null
    }
    const result = await CallNative.playRingbackTone({
      ...(input.url ? { url: input.url } : {}),
      ...(input.maxDurationMs ? { maxDurationMs: input.maxDurationMs } : {}),
      ...(input.callId ? { callId: input.callId } : {}),
    })
    const durationMs = result?.durationMs
    recordCallFlow('native:ringback:started', {
      durationMs,
      usage: result?.usage,
      stream: result?.stream,
      aaConnected: result?.aaConnected,
      uiModeCar: result?.uiModeCar,
      outputHasCar: result?.outputHasCar,
      leaveUnpinned: result?.leaveUnpinned,
      focusGranted: result?.focusGranted,
      owner: result?.owner ?? 'js',
      platform: getNativePlatform(),
    })
    if (Number.isFinite(durationMs) && durationMs > 0) {
      nativeRingbackActive = true
      return durationMs
    }
    nativeRingbackActive = false
    return null
  } catch (error) {
    nativeRingbackActive = false
    const msg = errorMessage(error)
    if (/not implemented|unimplemented|UNIMPLEMENTED/i.test(msg)) {
      nativeRingbackUnsupported = true
    }
    recordCallFlow('native:ringback:failed', {
      error: msg,
      platform: getNativePlatform(),
    })
    return null
  } finally {
    // The request settled (started, failed, or unsupported) — pending is over.
    nativeRingbackPending = false
  }
}

/** Native: stop the connecting-tone / ringback player. Safe no-op on web / older builds. */
export async function stopNativeConnectingTone(reason = 'js'): Promise<void> {
  nativeRingbackActive = false
  nativeRingbackPending = false
  if (!isNativeApp()) return
  try {
    await CallNative.stopConnectingTone?.({ reason })
  } catch {
    /* best-effort */
  }
}

/** False on older native builds that do not ship playConnectingTone. */
export function isNativeConnectingToneSupported(): boolean {
  return !nativeConnectingToneUnsupported
}

/**
 * Anchor the active call in the OS so it survives screen-off/backgrounding:
 * Android foreground service + wake locks; iOS CallKit outgoing-call report
 * (green pill/bar = system return-to-call). Pass callId for the iOS anchor.
 */
export type NativeOngoingCallResult = {
  /**
   * Android refused: this device already ended the call ('tombstoned'), or a
   * car End tapped before the dial had a callId cancelled it ('car-ended').
   */
  refused: boolean
  reason?: string
}

export async function startNativeOngoingCall(input: {
  video: boolean
  peerName: string
  callId?: string
  peerId?: string
  group?: boolean
  /** True ONLY at the caller's dial time (see NativeCallPluginApi.startOngoingCall). */
  dialing?: boolean
  /** The dial came from a car tap (a pending car End may apply to it). */
  fromCar?: boolean
}): Promise<NativeOngoingCallResult> {
  if (!isNativeApp()) return { refused: false }
  try {
    const result = await CallNative.startOngoingCall({
      video: input.video,
      peerName: input.peerName,
      callId: input.callId,
      peerId: input.peerId,
      group: input.group,
      dialing: input.dialing === true,
      fromCar: input.fromCar === true,
    })
    const refused = Boolean(result && typeof result === 'object' && result.refused === true)
    const reason = result && typeof result === 'object' ? result.reason : undefined
    return reason ? { refused, reason } : { refused }
  } catch {
    /* plugin missing or refused — call still runs, just without the service */
    return { refused: false }
  } finally {
    if (input.dialing && input.callId) nativeDialReport(input.callId).settle()
  }
}

/**
 * The call a native teardown belongs to. `carTapAt` (car tap time, epoch ms)
 * ties a car dial that has no native callId yet to its own car tap, so a
 * cancelled dial's late teardown never clears a car redial's state.
 */
export type NativeCallOwner = { callId: string; carTapAt?: number | null }

function carTapAtOption(carTapAt: number | null | undefined): { carTapAt?: number } {
  return carTapAt && carTapAt > 0 ? { carTapAt } : {}
}

/**
 * Android: the callee answered this outgoing dial. The native 118 s DIALING
 * stall backstop must not end the call while media is still connecting.
 */
export async function noteNativeOutgoingAnswered(callId: string): Promise<void> {
  if (!isNativeApp() || getNativePlatform() !== 'android' || !callId) return
  try {
    await CallNative.noteOutgoingAnswered?.({ callId })
  } catch {
    /* older shell — setCallActive at connect still moves it out of DIALING */
  }
}

/**
 * Android: tear down the OUTGOING Telecom connection of a dial that is being
 * abandoned before it connected. No-op on web / iOS / older shells.
 */
export async function endNativeOutgoingCall(
  callId: string,
  reason = 'canceled',
  carTapAt?: number | null,
): Promise<void> {
  if (!isNativeApp() || getNativePlatform() !== 'android' || !callId) return
  try {
    await CallNative.endOutgoingCall?.({ callId, reason, ...carTapAtOption(carTapAt) })
  } catch {
    /* older shell — finishCall's setCallDisconnected still covers teardown */
  }
}

/**
 * Mirror the signed-in user id into native (Android + iOS): a different user
 * than the last one wipes the previous account's native call state and ends
 * its rings / CallKit calls; native invites, acks and joins addressed to
 * anyone else are refused.
 */
export async function setNativeSignedInUser(userId: string): Promise<string | null> {
  if (!isNativeApp() || !userId) return null
  try {
    const result = await CallNative.setSignedInUser?.({ userId })
    return result?.transition ?? null
  } catch {
    return null
  }
}

/** Sign-out: wipe the signed-out account's native call state (Android + iOS). */
export async function clearNativeUserState(reason = 'signOut'): Promise<void> {
  if (!isNativeApp()) return
  await CallNative.clearUserState?.({ reason })
}

/** End the ongoing-call foreground service + release wake locks. */
export async function stopNativeOngoingCall(owner?: NativeCallOwner): Promise<void> {
  if (!isNativeApp()) return
  try {
    if (owner) {
      await CallNative.stopOngoingCall({ callId: owner.callId, ...carTapAtOption(owner.carTapAt) })
    } else {
      await CallNative.stopOngoingCall()
    }
  } catch {
    /* nothing to stop */
  }
}

/** Show the RingCentral-style full-screen incoming call (app process alive). */
export async function showNativeIncomingCall(input: {
  callId: string
  callerName: string
  video: boolean
  callerId?: string
  calleeId?: string
  /** 'normal' | 'on-call' | 'urgent' — surfaces an On-call / Urgent label. */
  callPurpose?: CallPurpose
}): Promise<boolean> {
  if (!isNativeApp()) return false
  try {
    await CallNative.showIncomingCall(input)
    markNativeIncomingRingActive(input.callId)
    return true
  } catch {
    return false
  }
}

/**
 * Dismiss the native full-screen incoming call UI for a given call.
 * reason 'answered' = the user answered on THIS device: Android promotes the
 * ringing Telecom connection to ACTIVE (never destroys it — the callee must
 * keep its INCOMING connection, see NativeCallPluginApi.cancelIncomingCall).
 */
export async function cancelNativeIncomingCall(
  callId: string,
  reason: 'answered' | 'canceled' = 'canceled',
  identity?: { peerId?: string; peerName?: string; callerName?: string },
): Promise<void> {
  if (!isNativeApp()) return
  clearNativeIncomingRingActive(callId)
  try {
    await CallNative.cancelIncomingCall({
      callId,
      reason,
      ...(identity?.peerId ? { peerId: identity.peerId } : {}),
      ...(identity?.peerName ? { peerName: identity.peerName } : {}),
      ...(identity?.callerName ? { callerName: identity.callerName } : {}),
    })
  } catch {
    /* plugin missing — nothing to dismiss */
  }
}

/**
 * Pull any pending Answer/Decline the user tapped while the WebView was paused
 * or starting (cold start via the notification). Returns `null` when nothing is
 * pending or the plugin is unavailable. Stale entries (older than `maxAgeMs`)
 * are ignored so a previous call can't auto-answer a fresh one.
 */
export async function consumeNativePendingCallAction(
  maxAgeMs = 60_000,
): Promise<{
  callId: string
  action: 'answer' | 'decline' | 'dismiss' | 'end'
  source?: string
  lockedAnswer?: boolean
} | null> {
  if (!isNativeApp()) return null
  try {
    const result = await CallNative.consumePendingCallAction()
    if (!result?.callId || !result.action) return null
    if (result.ts && Date.now() - result.ts > maxAgeMs) return null
    return {
      callId: result.callId,
      action: result.action,
      ...(result.source ? { source: result.source } : {}),
      ...(result.lockedAnswer === true ? { lockedAnswer: true } : {}),
    }
  } catch {
    return null
  }
}

export type NativePendingIncomingCall = {
  callId: string
  callerId?: string
  calleeId?: string
  callerName?: string
  mediaType: 'voice' | 'video'
  callPurpose?: CallPurpose
  /** Originating conversation thread (when the call started from a chat). */
  conversationId?: string
  /** True when the invite added this user to an existing group call. */
  roomInvite?: boolean
  /** Room/meeting id to join for a room invite (defaults to callId). */
  joinCallId?: string
}

/**
 * Pull any incoming-call invite the native FCM service seeded while the app was
 * killed/backgrounded, so JS can reconstruct the call session on cold start and
 * actually join when the user tapped Answer. Returns `null` when nothing is
 * pending, the plugin is unavailable, or the invite is staler than `maxAgeMs`
 * (a call that already rang out should never auto-resurrect).
 */
export async function consumeNativePendingIncomingCall(
  maxAgeMs = 60_000,
): Promise<NativePendingIncomingCall | null> {
  if (!isNativeApp()) return null
  try {
    const result = await CallNative.consumePendingIncomingCall()
    if (!result?.callId) return null
    if (result.ts && Date.now() - result.ts > maxAgeMs) return null
    const callPurpose: CallPurpose =
      result.callPurpose === 'on-call' || result.callPurpose === 'urgent'
        ? result.callPurpose
        : 'normal'
    markNativeIncomingRingActive(result.callId)
    return {
      callId: result.callId,
      callerId: result.callerId || undefined,
      calleeId: result.calleeId || undefined,
      callerName: result.callerName || undefined,
      mediaType: result.mediaType === 'video' ? 'video' : 'voice',
      callPurpose,
      conversationId: result.conversationId || undefined,
      roomInvite: result.roomInvite === true,
      joinCallId: result.joinCallId || undefined,
    }
  } catch {
    return null
  }
}

/**
 * Android 14+ gates USE_FULL_SCREEN_INTENT behind a per-app toggle. Without it
 * the incoming-call notification can't show its ringing full-screen UI and
 * degrades to a quiet heads-up banner. Returns true when granted (or < API 34,
 * or the plugin is unavailable so we don't nag on older APKs).
 */
export async function canUseFullScreenIntent(): Promise<boolean> {
  if (!isNativeApp()) return true
  try {
    const { granted } = await CallNative.canUseFullScreenIntent()
    return granted
  } catch {
    return true
  }
}

/** Deep-link the user to the system "Full screen intents" toggle (API 34+). */
export async function openFullScreenIntentSettings(): Promise<void> {
  if (!isNativeApp()) return
  try {
    await CallNative.openFullScreenIntentSettings()
  } catch {
    /* plugin missing or settings action unavailable */
  }
}

/** Whether the app is exempt from battery optimization (Doze allowlist). */
export async function isIgnoringBatteryOptimizations(): Promise<boolean> {
  if (!isNativeApp()) return true
  try {
    const { ignoring } = await CallNative.isIgnoringBatteryOptimizations()
    return ignoring
  } catch {
    return true
  }
}

/** Show the one-time system dialog to exempt the app from battery optimization. */
export async function requestIgnoreBatteryOptimizations(): Promise<void> {
  if (!isNativeApp()) return
  try {
    await CallNative.requestIgnoreBatteryOptimizations()
  } catch {
    /* plugin missing or OEM blocked the request action */
  }
}

/** `CallPlugin.INCOMING_CHANNEL_ID` — the channel incoming calls ring on. */
export const INCOMING_CALLS_CHANNEL_ID = 'incoming_calls'

/**
 * Whether Android's "Incoming calls" channel can ring. `unavailable` on iOS,
 * web, and builds without the method (Android 133 and older), so setup hides
 * the step instead of guessing.
 */
export async function getIncomingCallsChannelStatus(): Promise<'enabled' | 'disabled' | 'unavailable'> {
  if (!isNativeApp() || getNativePlatform() !== 'android') return 'unavailable'
  try {
    const { enabled } = await CallNative.getNotificationChannelStatus({ channelId: INCOMING_CALLS_CHANNEL_ID })
    if (typeof enabled !== 'boolean') return 'unavailable'
    return enabled ? 'enabled' : 'disabled'
  } catch {
    return 'unavailable'
  }
}

/** Deep-link to the "Incoming calls" channel settings; false when this build can't. */
export async function openIncomingCallsChannelSettings(): Promise<boolean> {
  if (!isNativeApp() || getNativePlatform() !== 'android') return false
  try {
    await CallNative.openNotificationChannelSettings({ channelId: INCOMING_CALLS_CHANNEL_ID })
    return true
  } catch {
    return false
  }
}

/** Register the self-managed Telecom PhoneAccount (idempotent; safe on web). */
export async function registerNativeTelecomAccount(): Promise<boolean> {
  if (!isNativeApp()) return false
  try {
    const { supported } = await CallNative.registerTelecomAccount()
    return Boolean(supported)
  } catch (error) {
    // Was previously swallowed silently — on iOS this is the JS→native call
    // that kicks off PushKit registration, so a rejection here (e.g. the
    // `CallNative` plugin not found on the bridge) would otherwise look
    // identical to "PushKit just hasn't produced a token yet" and be
    // impossible to diagnose from device logs alone.
    console.warn('[native-call] registerTelecomAccount FAILED', error)
    pushDiagnostics.note(
      `IOS_VOIP: native registerTelecomAccount() call REJECTED — ${errorMessage(error)}`,
    )
    return false
  }
}

/**
 * Tell the OS the call is connected — starts the status-bar call chip + timer
 * for the self-managed Telecom connection. No-op on web / notification-only.
 */
export async function setNativeCallActive(callId: string): Promise<void> {
  if (!isNativeApp() || !callId) return
  try {
    await CallNative.setCallActive({ callId })
  } catch {
    /* Telecom didn't accept this call — notification-only fallback */
  }
}

/**
 * Tell the OS the call ended so the status-bar chip / ringing call clears.
 * reason: 'rejected' | 'missed' | 'canceled' (default local hang-up).
 */
export async function setNativeCallDisconnected(
  callId: string,
  reason?: 'rejected' | 'missed' | 'canceled' | 'dismiss' | 'consent',
  carTapAt?: number | null,
): Promise<void> {
  if (!isNativeApp() || !callId) return
  clearNativeIncomingRingActive(callId)
  try {
    await CallNative.setCallDisconnected({ callId, reason, ...carTapAtOption(carTapAt) })
  } catch {
    /* nothing to disconnect */
  }
}

/**
 * Destroy leftover OS-call objects JS is not currently driving. No-op on web
 * and on older shells that lack sweepNativeCalls (build ≤105).
 */
export async function sweepNativeTelecomConnections(
  liveCallId?: string | null,
): Promise<void> {
  if (!isNativeApp()) return
  try {
    await CallNative.sweepNativeCalls?.({ liveCallId: liveCallId ?? undefined })
  } catch {
    /* older APK / plugin missing */
  }
}

/**
 * callIds the Android native layer knows the server ended. Empty on web / iOS
 * and on older shells without the method.
 */
export async function getNativeEndedCallIds(): Promise<ReadonlySet<string>> {
  if (!isNativeApp() || getNativePlatform() !== 'android') return new Set()
  try {
    const result = await CallNative.getRemoteEndedCallIds?.()
    return new Set((result?.callIds ?? []).filter((id) => typeof id === 'string' && id))
  } catch {
    return new Set()
  }
}

/**
 * Listen for Answer/Decline/End from the native full-screen call UI, the
 * CallStyle notification, OR the self-managed Telecom connection (lock screen,
 * Bluetooth headset, Android Auto, status-bar chip). Returns an unsubscribe fn.
 */
export async function addNativeCallActionListener(handlers: {
  /** `lockedAnswer` (Android): answered on the locked keyguard — video waits for unlock. */
  onAnswer: (callId: string, meta?: { lockedAnswer?: boolean }) => void
  onDecline: (callId: string) => void
  onDismiss?: (callId: string, source?: string) => void
  onEnd?: (callId: string, source?: string) => void
  /**
   * iOS CallKit mute toggle (CXSetMutedCallAction — lock screen, CarPlay,
   * AirPods). Without this the native audio is muted but the in-app mute
   * button keeps showing the stale state.
   */
  onMuteChanged?: (callId: string, muted: boolean) => void
}): Promise<() => void> {
  if (!isNativeApp()) return () => {}
  try {
    const handle = await CallNative.addListener('callAction', ({ callId, action, source, lockedAnswer }) => {
      if (!callId) return
      if (action === 'answer') {
        if (lockedAnswer === true) handlers.onAnswer(callId, { lockedAnswer: true })
        else handlers.onAnswer(callId)
      }
      else if (action === 'decline') handlers.onDecline(callId)
      else if (action === 'dismiss') handlers.onDismiss?.(callId, source)
      else if (action === 'end') {
        if (handlers.onEnd) handlers.onEnd(callId, source)
        else handlers.onDecline(callId)
      }
      else if (action === 'mute' || action === 'unmute') {
        handlers.onMuteChanged?.(callId, action === 'mute')
      }
    })
    // Listener-ready handshake (iOS): the listener is attached — ask native to
    // re-emit any pending CallKit Answer/End directly to it. Without this, a
    // cold-start answer relied on retained-event timing / a later app-resume
    // drain, which is exactly where lock-screen answers were getting lost.
    // Optional method: absent on Android (its flushPendingAction covers this).
    try {
      await CallNative.notifyCallActionListenerReady?.()
    } catch {
      /* older binary / Android — pending-action drain still covers replay */
    }
    return () => void handle.remove()
  } catch {
    return () => {}
  }
}

/**
 * Android: a self-managed Telecom connection was destroyed. `source` is the
 * native teardown origin (js, abort, disconnect, reject, dialing-stall, fcm,
 * notification, ring-timeout, sweep, other-call, unkeyed, refused,
 * user-cleared). Returns an unsubscribe function.
 */
export async function addNativeConnectionDestroyedListener(
  onDestroyed: (callId: string, source: string) => void,
): Promise<() => void> {
  if (!isNativeApp()) return () => {}
  try {
    const handle = await CallNative.addListener('connectionDestroyed', ({ callId, source }) => {
      if (callId) onDestroyed(callId, source ?? 'js')
    })
    return () => void handle.remove()
  } catch {
    return () => {}
  }
}

/**
 * iOS: CallKit reported (active=true) or released (active=false) the incoming
 * ring for a call. While active, CallKit is the ONE canonical ring surface —
 * the in-app incoming overlay and JS ringtone must stand down. Also keeps the
 * module-level native-ring registry in sync so `isNativeIncomingRingActive`
 * reflects CallKit rings that started while the app was foregrounded.
 * Never fired on Android. Returns an unsubscribe function.
 */
export async function addNativeIncomingRingListener(
  onChange: (callId: string, active: boolean) => void,
): Promise<() => void> {
  if (!isNativeApp()) return () => {}
  try {
    const handle = await CallNative.addListener('nativeIncomingRing', ({ callId, active }) => {
      if (!callId) return
      if (active) markNativeIncomingRingActive(callId)
      else clearNativeIncomingRingActive(callId)
      onChange(callId, active)
    })
    return () => void handle.remove()
  } catch {
    return () => {}
  }
}

/**
 * iOS (CallKit/PushKit): fetch the current PushKit VoIP token, then keep it
 * current via {@link addNativeVoipTokenListener}. The token is registered with
 * the backend as the IOS_VOIP push platform so call invites are delivered as
 * VoIP pushes (CallKit). Returns null on Android/web or before PushKit registers.
 */
export async function getNativeVoipToken(): Promise<string | null> {
  if (!isNativeApp()) return null
  try {
    const result = await CallNative.getVoipToken?.()
    const token = result?.token?.trim()
    return token ? token : null
  } catch (error) {
    // Was previously swallowed silently. If this throws (rather than just
    // resolving with an empty token), the bridge call itself failed — a
    // completely different failure mode than "PushKit hasn't minted a token
    // yet" and one `useIosVoipPush`'s "no token yet" note would otherwise
    // mask forever.
    console.warn('[native-call] getVoipToken FAILED', error)
    pushDiagnostics.note(`IOS_VOIP: native getVoipToken() call REJECTED — ${errorMessage(error)}`)
    return null
  }
}

/** Last VoIP token this session registered with the backend (sign-out unregister). */
let registeredVoipToken: string | null = null

export function noteRegisteredVoipToken(token: string | null): void {
  registeredVoipToken = token
}

/** Sign-out: hand over (and forget) the VoIP token this session registered. */
export function takeRegisteredVoipToken(): string | null {
  const token = registeredVoipToken
  registeredVoipToken = null
  return token
}

/**
 * Sign-out unregister: the live PushKit token, else the last token PushKit
 * delivered on this device (persisted natively) — so an unregister is not
 * skipped just because this launch's PushKit callback has not arrived yet.
 */
export async function getNativeVoipTokenForUnregister(): Promise<string | null> {
  if (!isNativeApp()) return null
  try {
    const result = await CallNative.getVoipToken?.()
    const token = result?.token?.trim() || result?.lastKnownToken?.trim()
    return token || null
  } catch {
    return null
  }
}

/** iOS: live CallKit call (if any). Null when none / Android / web / older binary. */
export async function getActiveCallKitCall(): Promise<{
  callId: string
  uuid?: string
  answered: boolean
  nativeMedia: boolean
  callerName?: string
  mediaType?: 'voice' | 'video'
  callPurpose?: CallPurpose
  callerId?: string
  calleeId?: string
  conversationId?: string
  roomInvite?: boolean
  joinCallId?: string
} | null> {
  if (!isNativeApp()) return null
  try {
    const result = await CallNative.getActiveCallKitCall?.()
    if (!result?.callId) return null
    const mediaType =
      result.mediaType === 'video' ? 'video' : result.mediaType === 'voice' ? 'voice' : undefined
    const purpose = result.callPurpose
    const callPurpose: CallPurpose | undefined =
      purpose === 'on-call' || purpose === 'urgent' || purpose === 'normal' ? purpose : undefined
    return {
      callId: result.callId,
      uuid: result.uuid || undefined,
      answered: Boolean(result.answered),
      nativeMedia: Boolean(result.nativeMedia),
      callerName: result.callerName?.trim() || undefined,
      mediaType,
      callPurpose,
      callerId: result.callerId?.trim() || undefined,
      calleeId: result.calleeId?.trim() || undefined,
      conversationId: result.conversationId?.trim() || undefined,
      roomInvite:
        result.roomInvite === true ||
        result.roomInvite === 'true' ||
        result.roomInvite === '1',
      joinCallId: result.joinCallId?.trim() || undefined,
    }
  } catch {
    return null
  }
}

/**
 * iOS: Dynamic Island / CallKit green-pill / unlock return. Unsubscribe on cleanup.
 */
export async function addNativeCallKitReturnListener(
  onReturn: (data: { callId: string; uuid?: string; nativeMedia: boolean }) => void,
): Promise<() => void> {
  if (!isNativeApp()) return () => {}
  try {
    const handle = await CallNative.addListener('callKitReturn', (data) => {
      if (!data?.callId) return
      onReturn({
        callId: data.callId,
        uuid: data.uuid,
        nativeMedia: Boolean(data.nativeMedia),
      })
    })
    return () => void handle.remove()
  } catch {
    return () => {}
  }
}

/** Subscribe to PushKit VoIP token updates (iOS). Returns an unsubscribe fn. */
export async function addNativeVoipTokenListener(
  onToken: (token: string) => void,
): Promise<() => void> {
  if (!isNativeApp()) return () => {}
  try {
    const handle = await CallNative.addListener('voipToken', ({ token }) => {
      if (token) onToken(token)
    })
    return () => void handle.remove()
  } catch (error) {
    // Was previously swallowed silently — if addListener itself rejects, the
    // hook never learns about a token minted after mount, which looks
    // identical to "PushKit hasn't produced a token yet" from the JS side.
    console.warn('[native-call] addListener(voipToken) FAILED', error)
    pushDiagnostics.note(
      `IOS_VOIP: native addListener('voipToken') call REJECTED — ${errorMessage(error)}`,
    )
    return () => {}
  }
}

/**
 * iOS only: fires when CallKit activates the shared AVAudioSession (after the
 * user answers a CallKit/VoIP call). The controller re-asserts the call audio
 * route on this so Chime's WebRTC audio unit ends up bound to the live, active
 * .playAndRecord session — fixing connected-but-silent CallKit calls (bug 2).
 * No-op on Android/web. Returns an unsubscribe function.
 */
export async function addNativeAudioSessionActivatedListener(
  onActivated: () => void,
): Promise<() => void> {
  if (!isNativeApp()) return () => {}
  try {
    const handle = await CallNative.addListener('callAudioSessionActivated', () => {
      onActivated()
    })
    return () => void handle.remove()
  } catch {
    return () => {}
  }
}

/**
 * iOS only: fires when CallKit RELEASES the shared AVAudioSession — i.e. the
 * ring-only CallKit call ended (immediate or deferred lock-screen handoff).
 * If an in-app call is live, its Chime audio may have bound while CallKit
 * still owned the session and be silent; the controller restarts audio input
 * and re-asserts the route on this. No-op on Android/web.
 */
export async function addNativeAudioSessionDeactivatedListener(
  onDeactivated: () => void,
): Promise<() => void> {
  if (!isNativeApp()) return () => {}
  try {
    const handle = await CallNative.addListener('callAudioSessionDeactivated', () => {
      onDeactivated()
    })
    return () => void handle.remove()
  } catch {
    return () => {}
  }
}

/**
 * Subscribe to live native call telemetry (AVAudioSession/CallKit on iOS,
 * AudioManager/Telecom on Android, app lifecycle, WKWebView content-process
 * termination). Each emission is a namespaced `event` label plus arbitrary
 * state fields; the call-flow telemetry buffer funnels these into the single
 * shipping path. Best-effort; returns a no-op unsubscribe on web/old shells.
 */
export async function addNativeCallTelemetryListener(
  onTelemetry: (payload: Record<string, unknown>) => void,
): Promise<() => void> {
  if (!isNativeApp()) return () => {}
  try {
    const handle = await CallNative.addListener('callTelemetry', (data) => {
      if (data && typeof data === 'object') onTelemetry(data)
    })
    return () => void handle.remove()
  } catch {
    return () => {}
  }
}

/**
 * Native connecting-tone player finished one playthrough (self-stop). JS marks
 * the disclosure complete so the compliance gate can release without waiting
 * out a duration timer. Older shells without the event are fine — the gate
 * times out at duration + slack.
 */
export async function addNativeConnectingToneCompleteListener(
  onComplete: (durationMs: number | null) => void,
): Promise<() => void> {
  if (!isNativeApp()) return () => {}
  try {
    const handle = await CallNative.addListener('connectingToneComplete', (data) => {
      const durationMs = Number(data?.durationMs)
      onComplete(Number.isFinite(durationMs) && durationMs > 0 ? durationMs : null)
    })
    return () => void handle.remove()
  } catch {
    return () => {}
  }
}

/**
 * Listen for OS audio-route changes on the self-managed Telecom connection
 * (e.g. a Bluetooth headset connecting mid-call) so the overlay's speaker
 * toggle tracks the real route. Returns an unsubscribe function.
 */
export async function addNativeAudioRouteListener(
  onRoute: (
    callId: string,
    route: 'earpiece' | 'speaker' | 'bluetooth' | 'headset',
    meta?: { userPick?: boolean },
  ) => void,
): Promise<() => void> {
  if (!isNativeApp()) return () => {}
  try {
    const handle = await CallNative.addListener('callAudioRoute', ({ callId, route, userPick }) => {
      if (!callId) return
      if (userPick === true) onRoute(callId, route, { userPick: true })
      else onRoute(callId, route)
    })
    return () => void handle.remove()
  } catch {
    return () => {}
  }
}

/**
 * Android: the device unlocked during a call answered on the lock screen
 * (video may turn on now). Returns an unsubscribe function.
 */
export async function addNativeDeviceUnlockedListener(
  onUnlocked: (callId: string, via: string) => void,
): Promise<() => void> {
  if (!isNativeApp() || getNativePlatform() !== 'android') return () => {}
  try {
    const handle = await CallNative.addListener('deviceUnlocked', ({ callId, via }) => {
      if (callId) onUnlocked(callId, via ?? 'native')
    })
    return () => void handle.remove()
  } catch {
    return () => {}
  }
}

/** Android: true / false when the shell reports the keyguard state, null when it cannot. */
export async function getNativeKeyguardLocked(): Promise<boolean | null> {
  if (!isNativeApp() || getNativePlatform() !== 'android') return null
  try {
    const result = await CallNative.getKeyguardState?.()
    return typeof result?.locked === 'boolean' ? result.locked : null
  } catch {
    return null
  }
}

export type NativeLockScreenCall = {
  callId: string
  phase: 'connecting' | 'active' | 'ended'
  muted: boolean
  speaker: boolean
  video: boolean
  connectedAt?: number
}

/** Android: mirror the live call for the native lock-screen call screen (best-effort). */
export async function updateNativeLockScreenCall(call: NativeLockScreenCall): Promise<void> {
  if (!isNativeApp() || getNativePlatform() !== 'android') return
  try {
    await CallNative.updateLockScreenCall?.(call)
  } catch {
    /* older shell without the lock-screen call screen */
  }
}

/** Persist a care-team peer for CarPlay / Android Auto History. */
export async function recordNativeCarRecent(input: {
  peerId: string
  peerName: string
  ts?: number
  avatarUrl?: string | null
}): Promise<void> {
  if (!isNativeApp() || !input.peerId) return
  try {
    await CallNative.recordCarRecent?.({
      peerId: input.peerId,
      peerName: input.peerName,
      ts: input.ts,
      avatarUrl: input.avatarUrl ?? undefined,
    })
  } catch {
    /* older binary */
  }
}

/** Replace the native CarPlay / Android Auto History snapshot. */
export async function replaceNativeCarRecents(
  recents: Array<{ peerId: string; peerName: string; ts?: number; avatarUrl?: string | null }>,
): Promise<void> {
  if (!isNativeApp()) return
  try {
    await CallNative.replaceCarRecents?.({ recents })
  } catch {
    /* older binary */
  }
}

/**
 * Replace the native CarPlay / Android Auto permitted-contacts snapshot.
 * `address`/`lat`/`lng` light up the car "Navigate" action when present —
 * the directory carries no addresses today, so they are normally omitted.
 */
export async function replaceNativeCarContacts(
  contacts: Array<{
    peerId: string
    peerName: string
    avatarUrl?: string | null
    address?: string
    lat?: number
    lng?: number
  }>,
): Promise<void> {
  if (!isNativeApp()) return
  try {
    await CallNative.replaceCarContacts?.({ contacts })
  } catch {
    /* older binary */
  }
}

/**
 * Replace the Android Auto Messages-tab snapshot (read-only recent
 * conversations: sender/thread title + a short preview of the latest
 * message). `peerId` only for DIRECT threads — it drives the Call action on
 * the car's safe-driving bubble. Push an empty array on sign-out.
 */
export async function replaceNativeCarMessages(
  messages: Array<{
    conversationId: string
    title: string
    preview: string
    ts: number
    peerId?: string
  }>,
): Promise<void> {
  if (!isNativeApp()) return
  try {
    await CallNative.replaceCarMessages?.({ messages })
  } catch {
    /* older binary */
  }
}

/**
 * Replace the Android Auto provider-sessions snapshot (tap-to-navigate
 * surface; destination is lat/lng when geocoded, else the address string).
 * Push an empty array for non-providers / on sign-out — the car entry hides.
 */
export async function replaceNativeCarSessions(
  sessions: Array<{
    sessionUuid: string
    title: string
    start: number
    end?: number
    address?: string
    lat?: number
    lng?: number
    cancelled?: boolean
  }>,
): Promise<void> {
  if (!isNativeApp()) return
  try {
    await CallNative.replaceCarSessions?.({ sessions })
  } catch {
    /* older binary */
  }
}

/** Wipe car recents on sign-out so the next account cannot see them. */
export async function clearNativeCarRecents(): Promise<void> {
  if (!isNativeApp()) return
  try {
    await CallNative.clearCarRecents?.()
  } catch {
    /* older binary */
  }
}

/**
 * Car-originated start failed (INVITE rejected). Native clears the dialing
 * template and, when `message` is set, shows the recording-policy alert.
 */
export async function notifyNativeCarStartFailed(input: {
  message?: string | null
  /** The start failed because another call is live: native keeps that call on the car. */
  alreadyOnCall?: boolean
}): Promise<void> {
  if (!isNativeApp()) return
  try {
    await CallNative.notifyCarStartFailed?.({
      message: input.message ?? '',
      ...(input.alreadyOnCall ? { alreadyOnCall: true } : {}),
    })
  } catch {
    /* older binary */
  }
}

/** Car taps already handed to the controller (live event or drained copy). */
const carStartTaps = createCarStartTapLedger()

/** Test hook. */
export function resetCarStartTapsForTests(): void {
  carStartTaps.reset()
}

/** Drain a car-screen tap-to-call queued while the WebView was not running. */
export async function consumeNativePendingCarStartCall(): Promise<{
  peerId: string
  peerName?: string
  ts?: number
} | null> {
  if (!isNativeApp()) return null
  try {
    const result = await CallNative.consumePendingCarStartCall?.()
    noteCarStartCancelledAt(result?.cancelledAt)
    if (!result?.peerId) return null
    if (!isCarStartCallFresh(result.ts)) return null
    if (isCarStartCancelled(result.ts, carStartCancelledAtMs)) return null
    if (!carStartTaps.claim(result.peerId, result.ts)) {
      recordCallFlow('native:carStart:duplicate', { via: 'drain', platform: getNativePlatform() })
      return null
    }
    return { peerId: result.peerId, peerName: result.peerName, ts: result.ts }
  } catch {
    return null
  }
}

/** Latest car End that cancelled a tap-to-call before it had a callId (epoch ms). */
let carStartCancelledAtMs = 0

function noteCarStartCancelledAt(ts: number | undefined): void {
  const at = Number(ts ?? 0)
  if (Number.isFinite(at) && at > carStartCancelledAtMs) carStartCancelledAtMs = at
}

/** A car tap made at `tapTs` was cancelled by an End at `cancelledAt` (or later). */
export function isCarStartCancelled(
  tapTs: number | undefined,
  cancelledAt: number | undefined,
): boolean {
  return Boolean(tapTs && cancelledAt && cancelledAt >= tapTs)
}

/** True once a car End has cancelled the tap-to-call made at `tapTs`. */
export function isNativeCarStartCancelledSince(tapTs: number | undefined): boolean {
  return isCarStartCancelled(tapTs, carStartCancelledAtMs)
}

/** Test hook. */
export function resetNativeCarStartCancelForTests(): void {
  carStartCancelledAtMs = 0
}

/** Car tap with no peer name (a 1:1 dial — never the group "Care team"). */
const CAR_START_FALLBACK_NAME = 'Ascend call'

/** Auto-dial guard (#343): drop car-tap events older than this. */
export const CAR_START_CALL_TTL_MS = 60_000

/**
 * True when a carStartCall event is fresh enough to auto-dial. Events carry
 * `ts` (tap time, epoch ms) from both the live native emit and the load()
 * flush of a persisted tap; anything past the TTL is a STALE retained event
 * (#343 — a days-old head-unit tap must never auto-dial on app launch).
 * Missing/zero `ts` (pre-105 binary live tap) is treated as fresh.
 */
export function isCarStartCallFresh(ts: number | undefined, now = Date.now()): boolean {
  if (!ts) return true
  return now - ts <= CAR_START_CALL_TTL_MS
}

/**
 * CarPlay / Android Auto recents tap. Does not foreground the WebView; the
 * controller starts the existing signaling path in-process.
 */
export async function addNativeCarStartCallListener(
  onStart: (peerId: string, peerName: string, tapTs: number) => void,
  onCancel?: (cancelledAt: number) => void,
): Promise<() => void> {
  if (!isNativeApp()) return () => {}
  try {
    // Read the persisted cancel time BEFORE attaching: a retained carStartCall
    // is delivered the moment the listener attaches and must be compared
    // against an End the driver already tapped.
    try {
      noteCarStartCancelledAt((await CallNative.getCarStartCancelledAt?.())?.ts)
    } catch {
      /* older binary */
    }
    const cancelHandle = await CallNative.addListener('carStartCancelled', ({ ts }) => {
      const at = Number(ts ?? 0) || Date.now()
      noteCarStartCancelledAt(at)
      onCancel?.(at)
    })
    const handle = await CallNative.addListener('carStartCall', ({ peerId, peerName, ts }) => {
      if (!peerId) return
      if (!isCarStartCallFresh(ts)) return
      if (isCarStartCancelled(ts, carStartCancelledAtMs)) return
      if (!carStartTaps.claim(peerId, ts)) {
        recordCallFlow('native:carStart:duplicate', { via: 'live', platform: getNativePlatform() })
        return
      }
      // Handled live: the saved copy must not be drained again on foreground.
      if (ts) void CallNative.ackCarStartCall?.({ ts }).catch(() => {})
      onStart(peerId, peerName || CAR_START_FALLBACK_NAME, ts || Date.now())
    })
    return () => {
      void handle.remove()
      void cancelHandle.remove()
    }
  } catch {
    return () => {}
  }
}
